import { Modal, ModalBackButton } from '@/components/Modal'
import styles from './Modal.module.scss'
import React, { memo, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import cl from 'classnames'
import { Timeline } from 'antd'
import { Icon } from '@/components/Icon'
import { DownloadIcon, Upload } from '@/app/icons'
import { useDispatch, useSelector } from 'react-redux'
import { sickLeaveSelectors, sickLeaveThunks, userSelectors } from '@/entity'
import { getSickLeaveStatuses } from '@/entity/sickLeave/consts'
import { Button } from '@/ui'

const CustomDot = ({ color }) => {
	return <div className={cl(styles.timelineDot)} style={{ background: color }} />
}

let color = {
	1: '#17B67C',
	2: '#eba834',
	3: '#ff0000',
	4: '#111',
}

// Компонент для отображения точки
const StatusDot = ({ status }) => {
	const color = status === '1' ? '#17B67C' : '#C4C4C4' // Зеленый или серый
	return (
		<div
			style={{
				width: '8px',
				height: '8px',
				borderRadius: '50%',
				backgroundColor: color,
				marginRight: '8px',
				display: 'inline-block',
			}}
		/>
	)
}

export const ViewModal = memo(({ onClose, item, onUpload, onDownload }) => {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const SICK_LEAVE_CURRENT_STATUSES = getSickLeaveStatuses()

	const { role, employerData } = useSelector((state) => ({
		role: userSelectors.activeRole(state),
		employerData: sickLeaveSelectors.employerData(state),
	}))

	// Добавляем флаг для проверки роли сотрудника
	const isEmployee = role === 'ROLE_EMPLOYEE'

	useEffect(() => {
		if (item.listId && isEmployee) {
			dispatch(sickLeaveThunks.getSendEmployerData(item.listId))
		}
	}, [])

	const information = useMemo(() => {
		let status1 = item?.chunck?.find((i) => i.status == 1)

		return [
			{
				label: t('issue_date'),
				desc: status1?.sinceReleasWrk,
			},
			{
				label: t('sickLeave.date_end_work'),
				desc: `${
					status1?.sinceReleasWrk && item.total?.tillReleasWrk
						? `${status1?.sinceReleasWrk}-${item.total?.tillReleasWrk}`
						: ``
				}`,
			},
			{
				label: t('sickLeave.start_date_work'),
				desc: item.total?.startWrk,
			},
			{
				label: t('sickLeave.diagnosis'),
				desc: item.total?.conclMKB10 || item.total?.mkb10,
			},
			{
				label: t('sickLeave.type_temporary_disability'),
				desc: item.total?.spcifyTempDis,
			},
			{
				label: t('sickLeave.mode_type'),
				desc: item.total?.typeRegime,
			},
		]
	}, [t])

	const infoTitle = {
		ROLE_EMPLOYEE: t('sickLeave.information'),
		ROLE_EMPLOYER: item.fio,
	}

	return (
		<Modal windowClassName={cl(styles.modal_wrapper, styles.modal_wrapper__send)}>
			<ModalBackButton onClick={onClose} />

			<div className={cl(styles.wrapper, 'scroll')}>
				<div className={cl(styles.header, 'mb-2')}>
					<h1 className={cl(styles.title, styles.titlebig)}>
						{t('sickLeave.number', { number: item.total?.number ? `№${item.total?.number}` : '' })}
					</h1>
					<span className={styles.subtitle}>{item.total?.status?.label}</span>
				</div>
				{isEmployee && (
					<div className={cl(styles.header)}>
						<span
							className={cl(styles.subtitle, {
								[styles.send_status]: item.total?.sendStatus?.value !== '0',
							})}
						>
							<StatusDot status={item.total?.sendStatus?.value} />
							{item.total?.sendStatus?.label}
						</span>
					</div>
				)}

				<div className={cl('flex', 'gap-4', 'mb-4', 'mt-4')}>
					{isEmployee && (
						<div>
							<Button onClick={onUpload}>
								<Icon component={Upload} className={cl('me-2', styles.icon)} />
								{t('actions.sendEmployer')}
							</Button>
						</div>
					)}

					{item?.total?.status?.value === '3' && (
						<div>
							<Button onClick={() => onDownload(item)}>
								<Icon component={DownloadIcon} className={cl('me-2', styles.icon)} />
								{t('labor_records.download')}
							</Button>
						</div>
					)}
				</div>

				<div className={cl(styles.body)}>
					<span className={cl(styles.med_title)}>{item.nameOfMo}</span>

					<div>
						<Timeline className={cl('mt-5', styles.timeline)}>
							{item.chunck?.map((chunck, chunckIdx) => {
								return (
									<Timeline.Item key={chunckIdx} dot={<CustomDot color={color[chunck.status]} />}>
										{/*<span className={styles.timeline__date}>{chunck?.dateOfIssue}</span>{' '}*/}
										<span className={styles.timeline__status}>{SICK_LEAVE_CURRENT_STATUSES[chunck.status]}</span>
									</Timeline.Item>
								)
							})}
						</Timeline>
					</div>

					<div>
						<span className={cl(styles.med_title)}>{infoTitle[role]}</span>

						<div className={cl(styles.info, 'mt-4')}>
							{information.map((info, infoIdx) => (
								<div key={infoIdx} className={cl(styles.info__item)}>
									<span className={cl(styles.info__title)}>
										{info.label}
										{':'}
									</span>
									<span className={cl(styles.info__subtitle)}>{info.desc}</span>
								</div>
							))}
						</div>
					</div>

					{employerData?.length > 0 && isEmployee ? (
						<>
							<div className={styles.employer}>
								{/*<Title>{t('sickLeave.title_sent_employer')}</Title>*/}
								<span className={cl(styles.med_title)}>{t('sickLeave.title_sent_employer')}</span>
							</div>

							<div className={cl(styles.info, styles['employer-info'], 'mt-4')}>
								{employerData.map((data, idx) => {
									const employerInfo = [
										{
											label: t('iin_bin'),
											desc: data.bin,
										},
										{
											label: t('name'),
											desc: data.orgName,
										},
									]

									return (
										<div key={idx}>
											{employerInfo.map((info, infoIdx) => (
												<div key={infoIdx} className={cl(styles.info__employer, 'flex')}>
													<span className={cl(styles.info__title)}>
														{info.label}
														{':'}
													</span>
													<span className={cl(styles.info__subtitle)}>{info.desc}</span>
												</div>
											))}
										</div>
									)
								})}
							</div>
						</>
					) : null}

					<div className={cl('mt-4', styles.footer)}>
						<Button onClick={onClose}>{t('close')}</Button>
					</div>
				</div>
			</div>
		</Modal>
	)
})
